import React, { useState, useContext, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  useGetDisciplinesQuery,
  useAssignPosteToDisciplineMutation,
} from '../../../services/SkillsManagement';
import {
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import CircularProgress from '@mui/material/CircularProgress';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import { ASSIGN_POSTE_TO_DISCIPLINE } from '../../../Reducers/Actions';

// Dialog to assign a position to a discipline
const AssignPosteToDiscipline = ({ row }) => {
  const dispatch = useDispatch();
  const [selectedDiscipline, setSelectedDiscipline] = useState('');
  const [close, setClose] = useState(false);

  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);

  // Fetch all disciplines (large page size to simulate no pagination)
  const { data: rawDisciplines, isLoading: disciplinesLoading } = useGetDisciplinesQuery(
    {
      filters: '',
      sorting: '',
      pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 1000 }),
    },
    { refetchOnMountOrArgChange: true }
  );

  const disciplineOptions = useMemo(() => {
    const list = Array.isArray(rawDisciplines?.data?.disciplines)
      ? rawDisciplines.data.disciplines
      : [];
    return list.map((d) => ({
      id: d.discipline_id ?? d.id ?? d.disciplineId,
      title: d.name,
    }));
  }, [rawDisciplines]);

  // Initialize selected discipline from current row assignment if present
  useEffect(() => {
    const original = row?.original || row || {};
    const assigned =
      original.discipline?.discipline_id ??
      original.discipline?.id ??
      original.discipline?.disciplineId ??
      original.discipline_id ??
      original.disciplineId ??
      null;
    if (assigned) {
      setSelectedDiscipline(assigned);
    }
  }, [row]);

  const [assignPositionToDiscipline, { isLoading: assigning }] = useAssignPosteToDisciplineMutation();

  const handleChange = (event) => setSelectedDiscipline(event.target.value);

  const handleSubmit = async () => {
    if (!selectedDiscipline) {
      setInfoAlert({ severity: 'error', message: 'Please select a departement to assign' });
      setOpenSnackBar(true);
      return;
    }

    const positionId = row?.original?.posteId ?? row?.original?.id ?? row?.posteId ?? row?.id;
    if (!positionId) {
      setInfoAlert({ severity: 'error', message: 'Invalid position selected' });
      setOpenSnackBar(true);
      return;
    }

    try {
      const result = await assignPositionToDiscipline({
        disciplineId: String(selectedDiscipline),
        posteId: String(positionId), // API still expects posteId
      }).unwrap();

      // Build discipline info for reducer payload (normalize id/name)
      const discipline =
        result?.discipline ||
        result?.data ||
        disciplineOptions.find((d) => String(d.id) === String(selectedDiscipline)) ||
        null;

      const normalizedDiscipline = discipline
        ? {
            discipline_id:
              discipline.discipline_id ?? discipline.id ?? discipline.disciplineId ?? Number(selectedDiscipline),
            name: discipline.name ?? discipline.title ?? 'Departement',
          }
        : null;

      dispatch({
        type: ASSIGN_POSTE_TO_DISCIPLINE, // Redux action unchanged
        payload: {
          posteId: Number(positionId),
          disciplineId: normalizedDiscipline?.discipline_id,
          discipline: normalizedDiscipline,
        },
      });

      setInfoAlert({ severity: 'success', message: 'Position successfully assigned to departement' });
      setOpenSnackBar(true);
      return true;
    } catch (error) {
      console.error('Error assigning position to departement:', error);
      setInfoAlert({ severity: 'error', message: error?.data?.message || 'Error assigning position to discipline' });
      setOpenSnackBar(true);
      return false;
    }
  };

  const positionName = row?.original?.name || row?.name || 'Selected Position';

  return (
    <Box>
      <MuiDialog
        icon={<SchoolIcon />}
        triggerButton={
          <MenuItem>
            <ListItemIcon>
              <SchoolIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign to Departement</ListItemText>
          </MenuItem>
        }
        title="Assign Position to Departement"
        subtitle={`Assigning ${positionName} to a Departement`}
        content={
          <Box sx={{ mt: 2, minWidth: '100%' }}>
            {disciplinesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <FormControl fullWidth required>
                <InputLabel id="discipline-select-label">Select Departement</InputLabel>
                <Select
                  labelId="discipline-select-label"
                  id="discipline-select"
                  value={selectedDiscipline}
                  label="Select Departement"
                  onChange={handleChange}
                  disabled={assigning}
                >
                  {disciplineOptions.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        }
        existAction={true}
        actionTitle={assigning ? 'Assigning...' : 'Assign'}
        actionFunction={handleSubmit}
        onClose={close}
      />
    </Box>
  );
};

export default AssignPosteToDiscipline;
